const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3060';
const MAIN_APP_URL = import.meta.env.VITE_MAIN_APP_URL || 'https://eureka-factory.com';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || 'http://localhost:5173';

// ============================================
// AUTH HELPERS
// ============================================

/**
 * Get auth token from localStorage
 * Returns null if no token exists (doesn't throw)
 */
function getToken(): string | null {
  return localStorage.getItem('token');
}

/**
 * Get auth token or throw error
 */
function requireToken(): string {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found. Please log in from the main Eureka app.');
  }
  return token;
}

/**
 * Verify navigation token with business-card service
 * Returns a session token for subsequent requests
 */
export async function verifyNavigationToken(navigationToken: string): Promise<{
  success: boolean;
  token: string;
  user: { userId: string; email?: string; name?: string };
}> {
  const res = await fetch(`${API_URL}/api/v1/scanned-cards/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: navigationToken })
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Token verification failed');
  }

  return res.json();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

// ============================================
// TYPES
// ============================================

export interface ScannedBusinessCard {
  id: string;
  ownerId: string;
  projectId?: string | null;

  // Extracted Information
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  title?: string | null;
  department?: string | null;
  company?: string | null;

  // Contact Details
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  fax?: string | null;
  website?: string | null;

  // Address
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;

  // Social Profiles
  socialLinks?: {
    linkedin?: string | null;
    twitter?: string | null;
    facebook?: string | null;
    instagram?: string | null;
  } | null;

  // Additional Info
  notes?: string | null;

  // OCR Processing
  originalImageUrl?: string | null;
  thumbnailUrl?: string | null;
  ocrConfidence?: number | null;
  ocrRawData?: any;

  // Processing Status
  processingStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'NEEDS_REVIEW';
  errorMessage?: string | null;

  // CRM Integration
  crmPersonId?: string | null;
  crmCompanyId?: string | null;
  leadId?: string | null;
  syncStatus: 'UNSYNCED' | 'SYNCING' | 'SYNCED' | 'FAILED' | 'SKIPPED';
  lastSyncedAt?: Date | null;

  // Metadata
  scanDate: Date;
  meetingDate?: Date | null;
  meetingContext?: string | null;
  source: 'MANUAL' | 'BATCH_IMPORT' | 'EMAIL_SCAN' | 'WEBHOOK';

  // Duplicates
  duplicateOf?: string | null;
  isDuplicate: boolean;
  duplicateReason?: string | null;

  createdAt: Date;
  updatedAt: Date;

  // Relations
  tags: ScannedCardTag[];
}

export interface ScannedCardTag {
  id: string;
  cardId: string;
  tag: string;
  color?: string | null;
}

export interface DuplicateCandidate {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  email: string | null;
  company: string | null;
  phone: string | null;
  mobile: string | null;
  matchReason: string[];
  matchScore: number;
}

export interface UploadResult {
  cardId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  error?: string;
}

export interface CardStatistics {
  totalCards: number;
  pendingCards: number;
  completedCards: number;
  failedCards: number;
  duplicateCards: number;
  syncedCards: number;
  uniqueCompanies: number;
  recentUploads: Array<{
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    createdAt: Date;
    processingStatus: string;
  }>;
}

export interface CardListResponse {
  data: ScannedBusinessCard[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// API CLIENT
// ============================================

/**
 * Helper to make authenticated API calls
 */
async function authenticatedFetch(url: string, options?: RequestInit): Promise<Response> {
  const token = requireToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
}

export const scannedCardsApi = {
  /**
   * Upload business card images for OCR
   */
  upload: async (files: File[], options?: {
    projectId?: string;
    meetingDate?: Date;
    meetingContext?: string;
    autoSync?: boolean;
  }): Promise<UploadResult[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    if (options?.projectId) formData.append('projectId', options.projectId);
    if (options?.meetingDate) formData.append('meetingDate', options.meetingDate.toISOString());
    if (options?.meetingContext) formData.append('meetingContext', options.meetingContext);
    if (options?.autoSync) formData.append('autoSync', 'true');

    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Upload failed');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * List all scanned cards
   */
  list: async (params?: {
    query?: string;
    page?: number;
    limit?: number;
    company?: string;
    title?: string;
    status?: string;
    sync?: string;
    duplicates?: boolean;
    tags?: string[];
  }): Promise<CardListResponse> => {
    const searchParams = new URLSearchParams();

    if (params?.query) searchParams.append('q', params.query);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.company) searchParams.append('company', params.company);
    if (params?.title) searchParams.append('title', params.title);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.sync) searchParams.append('sync', params.sync);
    if (params?.duplicates !== undefined) searchParams.append('duplicates', params.duplicates.toString());
    if (params?.tags?.length) searchParams.append('tags', params.tags.join(','));

    const queryString = searchParams.toString();
    const url = `${API_URL}/api/v1/scanned-cards${queryString ? `?${queryString}` : ''}`;

    const res = await authenticatedFetch(url);
    if (!res.ok) throw new Error('Failed to fetch cards');

    return res.json();
  },

  /**
   * Get single card
   */
  get: async (id: string): Promise<ScannedBusinessCard> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}`);
    if (!res.ok) throw new Error('Failed to fetch card');

    const data = await res.json();
    return data.data;
  },

  /**
   * Update card
   */
  update: async (id: string, updates: Partial<ScannedBusinessCard>): Promise<ScannedBusinessCard> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Update failed');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * Delete card
   */
  delete: async (id: string): Promise<void> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}`, {
      method: 'DELETE',
    });

    if (!res.ok) throw new Error('Failed to delete card');
  },

  /**
   * Find duplicate cards
   */
  findDuplicates: async (id: string): Promise<DuplicateCandidate[]> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/duplicates`);
    if (!res.ok) throw new Error('Failed to find duplicates');

    const data = await res.json();
    return data.data;
  },

  /**
   * Merge cards
   */
  merge: async (id: string, mergeIds: string[]): Promise<any> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mergeIds }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Merge failed');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * Add tag to card
   */
  addTag: async (id: string, tag: string, color?: string): Promise<ScannedCardTag> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, color }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to add tag');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * Remove tag from card
   */
  removeTag: async (id: string, tag: string): Promise<void> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    });

    if (!res.ok) throw new Error('Failed to remove tag');
  },

  /**
   * Get all user tags
   */
  getTags: async (): Promise<ScannedCardTag[]> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/tags`);
    if (!res.ok) throw new Error('Failed to fetch tags');

    const data = await res.json();
    return data.data;
  },

  /**
   * Share card
   */
  share: async (id: string, sharedWith: string, shareType: 'USER' | 'TEAM' | 'PROJECT', permission: 'VIEW' | 'EDIT' | 'DELETE' | 'OWNER', expiresAt?: Date): Promise<any> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sharedWith, shareType, permission, expiresAt: expiresAt?.toISOString() }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to share card');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * Get cards shared with user
   */
  getShared: async (): Promise<ScannedBusinessCard[]> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/shared`);
    if (!res.ok) throw new Error('Failed to fetch shared cards');

    const data = await res.json();
    return data.data;
  },

  /**
   * Sync to CRM
   */
  syncToCRM: async (id: string): Promise<any> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/${id}/sync`, {
      method: 'POST',
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'CRM sync failed');
    }

    const data = await res.json();
    return data.data;
  },

  /**
   * Get batch processing status
   */
  getBatchStatus: async (ids: string[]): Promise<any[]> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/batch-status?ids=${ids.join(',')}`);
    if (!res.ok) throw new Error('Failed to get batch status');

    const data = await res.json();
    return data.data;
  },

  /**
   * Get statistics
   */
  getStatistics: async (): Promise<CardStatistics> => {
    const res = await authenticatedFetch(`${API_URL}/api/v1/scanned-cards/statistics`);
    if (!res.ok) throw new Error('Failed to fetch statistics');

    const data = await res.json();
    return data.data;
  },
};
